import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Building2,
  Users as UsersIcon,
  ArrowRight,
  Sparkles,
  Inbox,
  ListChecks,
  FileEdit,
  FileText,
  Cog,
  Eye,
  Ban,
  CheckCircle2,
  Filter as FilterIcon,
  ChevronDown,
} from 'lucide-react';
import { tasksApi } from '../../api/tasks';
import TaskCreate from './TaskCreate';
import ConfirmDialog, { ConfirmState } from '../../components/ui/ConfirmDialog';

type SubTab = 'client' | 'vendor';

interface ClientRow {
  // Incoming cross-org draft awaiting my review — i.e. a task a client sent me.
  // Tasks I created for my own clients are intentionally excluded; those live
  // in the regular All / My Tasks views.
  id: string;
  title: string;
  status: string;
  priority?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  created_by?: string | null;
  requester_name?: string | null;
  requester_org?: string | null;
}

interface VendorRow {
  id: string;
  title: string;
  status: string;
  priority?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  target_org_name?: string | null;
  target_head_office_id?: string | null;
  direction?: 'outbound' | 'inbound' | null;
}

const PRIORITY_PILL: Record<string, string> = {
  URGENT: 'is-urgent',
  HIGH: 'is-high',
  MEDIUM: 'is-medium',
  LOW: 'is-low',
};

const STATUS_PILL: Record<string, string> = {
  DRAFT: 'is-ghost',
  TODO: 'is-pending',
  IN_PROGRESS: 'is-progress',
  IN_REVIEW: 'is-personal',
  REVIEW: 'is-personal',
  DONE: 'is-done',
  COMPLETED: 'is-done',
  CANCELLED: 'is-overdue',
  REJECTED: 'is-overdue',
  ON_HOLD: 'is-pending',
  BLOCKED: 'is-pending',
};

function pillClass(map: Record<string, string>, key?: string | null) {
  if (!key) return 'is-ghost';
  return map[key.toUpperCase()] || 'is-ghost';
}

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s;
  }
}

// Per-user "I've already seen this draft" tracker. Persisted in localStorage so
// the badge clears across reloads. A custom window event ('teamlio:draft-seen-
// changed') notifies all in-tab consumers when the set changes; the native
// 'storage' event syncs across tabs.
const SEEN_KEY = 'draft_seen_v1';
const SEEN_EVENT = 'teamlio:draft-seen-changed';

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSeen(set: Set<string>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
  } catch {
    // localStorage may be unavailable (private mode / quota) — non-fatal.
  }
}

export function useSeenDrafts() {
  const [seen, setSeen] = useState<Set<string>>(() => readSeen());
  useEffect(() => {
    const sync = () => setSeen(readSeen());
    window.addEventListener(SEEN_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SEEN_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const markSeen = (id: string) => {
    setSeen((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeSeen(next);
      // Notify other useSeenDrafts() consumers in this tab (e.g. TaskList's
      // badge) since 'storage' only fires across tabs.
      window.dispatchEvent(new Event(SEEN_EVENT));
      return next;
    });
  };
  return { seen, markSeen };
}

// Combined unseen count across Vendor + Client sub-tabs, for use by the parent
// TaskList Draft Tasks scope-tab badge. Polls both list endpoints every 60s.
export function useDraftUnseenTotal(enabled: boolean): number {
  const { seen } = useSeenDrafts();
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [vendorIds, setVendorIds] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled) {
      setClientIds([]);
      setVendorIds([]);
      return;
    }
    let cancelled = false;
    const fetchBoth = async () => {
      try {
        const [c, v] = await Promise.all([
          tasksApi.listDrafts(),
          tasksApi.listSentDrafts(),
        ]);
        if (cancelled) return;
        setClientIds(Array.isArray(c.data) ? c.data.map((r: any) => r.id).filter(Boolean) : []);
        setVendorIds(Array.isArray(v.data) ? v.data.map((r: any) => r.id).filter(Boolean) : []);
      } catch {
        // Network blip — keep stale data so the badge doesn't flicker.
      }
    };
    fetchBoth();
    const id = window.setInterval(fetchBoth, 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [enabled]);

  return useMemo(() => {
    let n = 0;
    for (const id of clientIds) if (!seen.has(id)) n++;
    for (const id of vendorIds) if (!seen.has(id)) n++;
    return n;
  }, [clientIds, vendorIds, seen]);
}

function fmtRelative(s?: string | null) {
  if (!s) return '';
  try {
    const d = new Date(s);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

// Narrow each card row to the fields the status/priority filters need so the
// same helper works for both Vendor and Client tabs.
interface Filterable {
  status: string;
  priority?: string | null;
}

const STATUS_BUCKETS: Record<string, (s: string) => boolean> = {
  ALL: () => true,
  DRAFT: (s) => s === 'DRAFT',
  TODO: (s) => s === 'TODO',
  IN_PROGRESS: (s) => s === 'IN_PROGRESS',
  IN_REVIEW: (s) => s === 'IN_REVIEW' || s === 'REVIEW',
  HOLD: (s) => s === 'ON_HOLD' || s === 'BLOCKED',
  COMPLETED: (s) => s === 'DONE' || s === 'COMPLETED',
};

function applyStatusPriority<T extends Filterable>(
  rows: T[],
  statusFilter: string,
  priorityFilter: string,
): T[] {
  const statusFn = STATUS_BUCKETS[statusFilter] || (() => true);
  return rows.filter((r) => {
    if (!statusFn((r.status || '').toUpperCase())) return false;
    if (priorityFilter && (r.priority || '').toUpperCase() !== priorityFilter) return false;
    return true;
  });
}

export default function DraftTasksList() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<SubTab>('client');
  const [clientRows, setClientRows] = useState<ClientRow[]>([]);
  const [vendorRows, setVendorRows] = useState<VendorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { seen, markSeen } = useSeenDrafts();

  const loadClientTab = async () => {
    setLoading(true);
    try {
      // Only incoming cross-org drafts — tasks a client actually sent me.
      const draftsRes = await tasksApi.listDrafts();
      const rows: ClientRow[] = Array.isArray(draftsRes.data) ? draftsRes.data : [];
      rows.sort((a, b) => {
        const aTime = new Date(a.last_activity_at || a.created_at || 0).getTime();
        const bTime = new Date(b.last_activity_at || b.created_at || 0).getTime();
        return bTime - aTime;
      });
      setClientRows(rows);
    } catch {
      setClientRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVendorTab = async () => {
    setLoading(true);
    try {
      const res = await tasksApi.listSentDrafts();
      setVendorRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setVendorRows([]);
    } finally {
      setLoading(false);
    }
  };

  const reload = () => {
    // Load BOTH sub-tabs so the Status tiles + Filters reflect combined
    // counts across Client and Vendor. Switching tabs is now a pure list
    // filter, no extra fetch — and counts stay visible when one sub-tab
    // happens to be empty.
    loadClientTab();
    loadVendorTab();
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Status tiles + Filters always see the union of Client + Vendor rows.
  // (Sub-tabs filter the list section, not the headline counts.)
  const baseRows: Filterable[] = useMemo(
    () => [...(clientRows as Filterable[]), ...(vendorRows as Filterable[])],
    [clientRows, vendorRows],
  );

  const filteredClient = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pq = partnerFilter.trim().toLowerCase();
    let rows = applyStatusPriority(clientRows, statusFilter, priorityFilter);
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.title || '').toLowerCase().includes(q) ||
          (r.requester_name || '').toLowerCase().includes(q) ||
          (r.requester_org || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q),
      );
    }
    if (pq) {
      rows = rows.filter(
        (r) =>
          (r.requester_org || '').toLowerCase().includes(pq) ||
          (r.requester_name || '').toLowerCase().includes(pq),
      );
    }
    return rows;
  }, [clientRows, query, statusFilter, priorityFilter, partnerFilter]);

  const filteredVendor = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pq = partnerFilter.trim().toLowerCase();
    let rows = applyStatusPriority(vendorRows, statusFilter, priorityFilter);
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.title || '').toLowerCase().includes(q) ||
          (r.target_org_name || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q),
      );
    }
    if (pq) {
      rows = rows.filter((r) => (r.target_org_name || '').toLowerCase().includes(pq));
    }
    return rows;
  }, [vendorRows, query, statusFilter, priorityFilter, partnerFilter]);

  const visibleCount = tab === 'client' ? filteredClient.length : filteredVendor.length;

  // Per-sub-tab unseen counts — drive the red badges on the Vendor / Client buttons.
  const clientUnseen = useMemo(
    () => clientRows.reduce((n, r) => n + (seen.has(r.id) ? 0 : 1), 0),
    [clientRows, seen],
  );
  const vendorUnseen = useMemo(
    () => vendorRows.reduce((n, r) => n + (seen.has(r.id) ? 0 : 1), 0),
    [vendorRows, seen],
  );

  const activeFiltersCount =
    (statusFilter !== 'ALL' ? 1 : 0) +
    (priorityFilter ? 1 : 0) +
    (partnerFilter.trim() ? 1 : 0);

  const resetFilters = () => {
    setStatusFilter('ALL');
    setPriorityFilter('');
    setPartnerFilter('');
  };

  return (
    <div className="space-y-5">
      {/* Status tiles — hidden while loading to avoid a 0-count flash.
          Always render once data is loaded (even at zero) so the layout
          stays stable when switching between Client / Vendor sub-tabs. */}
      {!loading && (
        <DraftStatusTiles
          rows={baseRows}
          activeStatus={statusFilter}
          onPickStatus={setStatusFilter}
        />
      )}

      {/* Filters (collapsible) — always available once loaded. */}
      {!loading && (
        <DraftFilters
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((o) => !o)}
          activeCount={activeFiltersCount}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          priority={priorityFilter}
          onPriorityChange={setPriorityFilter}
          partner={partnerFilter}
          onPartnerChange={setPartnerFilter}
          onReset={resetFilters}
        />
      )}

      {/* Sub-tabs (Client / Vendor) — same visual language as the parent scope tabs. */}
      <div className="gt-tasks-scope-tabs" style={{ borderBottom: '1px solid var(--gt-border)' }}>
        <nav className="flex gap-1 flex-nowrap overflow-x-auto">
          {[
            { id: 'client' as SubTab, label: 'Client', icon: <UsersIcon className="w-4 h-4" />, unseen: clientUnseen },
            { id: 'vendor' as SubTab, label: 'Vendor', icon: <Building2 className="w-4 h-4" />, unseen: vendorUnseen },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`gt-tasks-scope-tab transition-all${active ? ' is-active' : ''}`}
                style={{ marginBottom: -1 }}
              >
                {t.icon}
                {t.label}
                {t.unseen > 0 && <UnseenBadge count={t.unseen} />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Toolbar: result count · search · refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="gt-page-sub gt-numerals">
          {loading ? (
            <span className="gt-serif-italic">Loading&hellip;</span>
          ) : (
            <>
              Showing{' '}
              <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                {visibleCount}
              </span>{' '}
              {visibleCount === 1 ? 'task' : 'tasks'}
            </>
          )}
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="gt-search-wrap" style={{ width: 260, maxWidth: '100%' }}>
            <Search />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, partner, status"
              className="gt-input is-search"
              style={{ height: 38 }}
            />
          </div>
          <button
            type="button"
            onClick={reload}
            className="gt-btn is-ghost is-sm"
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="gt-loading-ring mb-3" />
          <p className="gt-page-sub gt-serif-italic">
            Gathering <em>partner</em> tasks&hellip;
          </p>
        </div>
      ) : tab === 'client' ? (
        <ClientCardGrid
          rows={filteredClient}
          seen={seen}
          onReview={(id) => {
            markSeen(id);
            setReviewing(id);
          }}
        />
      ) : (
        <VendorCardGrid
          rows={filteredVendor}
          seen={seen}
          onOpen={(row) => {
            markSeen(row.id);
            navigate(`/partner-task/${row.id}`);
          }}
          onReload={reload}
          onConfirm={setConfirm}
        />
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />

      {reviewing && (
        <TaskCreate
          isOpen={true}
          mode="admin-review"
          draftTaskId={reviewing}
          onClose={() => setReviewing(null)}
          onSuccess={() => {
            setReviewing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status tiles — All / Draft / To Do / In Progress / In Review / Hold / Done
// ---------------------------------------------------------------------------

type TileTone = 'all' | 'draft' | 'todo' | 'progress' | 'review' | 'blocked' | 'done';

const TILE_TONE: Record<TileTone, {
  bg: string; border: string; iconBg: string; iconFg: string; label: string; value: string;
}> = {
  all:      { bg: '#d1fae5', border: '#86efac', iconBg: '#bbf7d0', iconFg: '#047857', label: '#047857', value: '#064e3b' },
  draft:    { bg: '#f1f5f9', border: '#cbd5e1', iconBg: '#e2e8f0', iconFg: '#475569', label: '#475569', value: '#0f172a' },
  todo:     { bg: '#e2e8f0', border: '#cbd5e1', iconBg: '#cbd5e1', iconFg: '#334155', label: '#334155', value: '#0f172a' },
  progress: { bg: '#dbeafe', border: '#93c5fd', iconBg: '#bfdbfe', iconFg: '#1d4ed8', label: '#1e40af', value: '#1e3a8a' },
  review:   { bg: '#fef3c7', border: '#fcd34d', iconBg: '#fde68a', iconFg: '#b45309', label: '#92400e', value: '#78350f' },
  blocked:  { bg: '#fee2e2', border: '#fca5a5', iconBg: '#fecaca', iconFg: '#b91c1c', label: '#b91c1c', value: '#7f1d1d' },
  done:     { bg: '#bbf7d0', border: '#4ade80', iconBg: '#86efac', iconFg: '#065f46', label: '#065f46', value: '#064e3b' },
};

function DraftStatusTiles({
  rows,
  activeStatus,
  onPickStatus,
}: {
  rows: Filterable[];
  activeStatus: string;
  onPickStatus: (key: string) => void;
}) {
  const count = (fn: (s: string) => boolean) =>
    rows.filter((r) => fn((r.status || '').toUpperCase())).length;

  const tiles: Array<{ key: string; label: string; value: number; icon: React.ReactNode; tone: TileTone }> = [
    { key: 'ALL',         label: 'All',         value: rows.length,                       icon: <ListChecks className="w-3.5 h-3.5" />,   tone: 'all' },
    { key: 'DRAFT',       label: 'Draft',       value: count((s) => s === 'DRAFT'),       icon: <FileText className="w-3.5 h-3.5" />,     tone: 'draft' },
    { key: 'TODO',        label: 'To Do',       value: count((s) => s === 'TODO'),        icon: <FileEdit className="w-3.5 h-3.5" />,     tone: 'todo' },
    { key: 'IN_PROGRESS', label: 'In Progress', value: count((s) => s === 'IN_PROGRESS'), icon: <Cog className="w-3.5 h-3.5" />,          tone: 'progress' },
    { key: 'IN_REVIEW',   label: 'In Review',   value: count((s) => s === 'IN_REVIEW' || s === 'REVIEW'), icon: <Eye className="w-3.5 h-3.5" />, tone: 'review' },
    { key: 'HOLD',        label: 'Hold',        value: count((s) => s === 'ON_HOLD' || s === 'BLOCKED'),  icon: <Ban className="w-3.5 h-3.5" />, tone: 'blocked' },
    { key: 'COMPLETED',   label: 'Completed',   value: count((s) => s === 'DONE' || s === 'COMPLETED'),   icon: <CheckCircle2 className="w-3.5 h-3.5" />, tone: 'done' },
  ];

  return (
    <div
      className="gt-task-stats-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8,
        ['--task-stats-desktop-cols' as any]: tiles.length,
      } as React.CSSProperties}
    >
      {tiles.map(({ key, label, value, icon, tone }) => {
        const t = TILE_TONE[tone];
        const isActive = activeStatus === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onPickStatus(key)}
            className="text-left transition-all"
            style={{
              background: t.bg,
              border: `1px solid ${isActive ? t.iconFg : t.border}`,
              outline: isActive ? `2px solid ${t.iconFg}` : 'none',
              outlineOffset: isActive ? 2 : 0,
              borderRadius: 12,
              padding: '8px 10px',
              cursor: 'pointer',
              boxShadow: 'var(--gt-elev-1)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = 'var(--gt-elev-2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--gt-elev-1)';
            }}
          >
            <span
              style={{
                width: 22, height: 22, borderRadius: 7,
                background: t.iconBg, color: t.iconFg,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </span>
            <div className="flex flex-col min-w-0" style={{ lineHeight: 1.1 }}>
              <span
                className="gt-eyebrow"
                style={{ marginBottom: 0, color: t.label, fontSize: 9, letterSpacing: '0.06em' }}
              >
                {label}
              </span>
              <span
                className="gt-display gt-numerals"
                style={{
                  fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em',
                  color: t.value, marginTop: 1,
                }}
              >
                {value}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters card — collapsible: Status / Priority / Partner
// ---------------------------------------------------------------------------

function DraftFilters({
  isOpen,
  onToggle,
  activeCount,
  status,
  onStatusChange,
  priority,
  onPriorityChange,
  partner,
  onPartnerChange,
  onReset,
}: {
  isOpen: boolean;
  onToggle: () => void;
  activeCount: number;
  status: string;
  onStatusChange: (v: string) => void;
  priority: string;
  onPriorityChange: (v: string) => void;
  partner: string;
  onPartnerChange: (v: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="gt-card overflow-hidden">
      <button
        type="button"
        className="flex justify-between items-center p-4 w-full transition-colors"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <span
            style={{
              width: 32, height: 32, borderRadius: 11,
              background: 'var(--gt-primary-container)', color: 'var(--gt-primary)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FilterIcon className="w-3.5 h-3.5" />
          </span>
          <span
            className="gt-display"
            style={{ fontSize: 14, fontWeight: 700, color: 'var(--gt-text-primary)' }}
          >
            Filters
          </span>
          {activeCount > 0 && (
            <span className="gt-pill is-personal gt-numerals">{activeCount} active</span>
          )}
        </div>
        <ChevronDown
          className="w-4 h-4 transition-transform"
          style={{
            color: 'var(--gt-text-tertiary)',
            transform: isOpen ? 'rotate(180deg)' : 'none',
          }}
        />
      </button>

      {isOpen && (
        <div
          className="p-5 space-y-5 gt-fade-in"
          style={{ borderTop: '1px solid var(--gt-divider)', background: 'var(--gt-surface-1)' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="gt-field-label">Status</label>
              <select
                className="gt-select"
                value={status}
                onChange={(e) => onStatusChange(e.target.value)}
              >
                <option value="ALL">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="HOLD">Hold</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>

            <div>
              <label className="gt-field-label">Priority</label>
              <select
                className="gt-select"
                value={priority}
                onChange={(e) => onPriorityChange(e.target.value)}
              >
                <option value="">All Priority</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>

            <div>
              <label className="gt-field-label">Partner</label>
              <input
                type="text"
                value={partner}
                onChange={(e) => onPartnerChange(e.target.value)}
                placeholder="Company or person…"
                className="gt-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end items-center gap-3 pt-1">
            <button type="button" onClick={onReset} className="gt-btn is-ghost">
              Reset
            </button>
            <button type="button" onClick={onToggle} className="gt-btn is-primary">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable card primitives
// ---------------------------------------------------------------------------

export function UnseenBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={`${count} unseen`}
      className="gt-numerals"
      style={{
        marginLeft: 6,
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 9999,
        background: '#dc2626',
        color: '#fff',
        fontSize: 10,
        fontWeight: 800,
        lineHeight: '18px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NewDot() {
  return (
    <span
      title="New"
      aria-label="Unseen"
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#dc2626',
        flexShrink: 0,
        boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.12)',
      }}
    />
  );
}

function StatusPill({ status }: { status?: string | null }) {
  return (
    <span className={`gt-pill ${pillClass(STATUS_PILL, status)}`}>
      {(status || '—').replace(/_/g, ' ').toLowerCase()}
    </span>
  );
}

function PriorityPill({ priority }: { priority?: string | null }) {
  const cls = pillClass(PRIORITY_PILL, priority);
  return (
    <span className={`gt-pill ${cls}`}>
      <span className="gt-dot" />
      {(priority || '—').toLowerCase()}
    </span>
  );
}

function MicroLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="gt-eyebrow"
      style={{ marginBottom: 2, fontSize: 10, letterSpacing: '0.10em' }}
    >
      {children}
    </div>
  );
}

function EmptyState({
  title,
  subtitle,
  icon,
}: {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="gt-card p-12 text-center">
      <div className="gt-empty">
        <div className="gt-empty-leaf">{icon || <Sparkles className="w-6 h-6" />}</div>
        <div className="gt-empty-title">{title}</div>
        <div className="gt-page-sub">{subtitle}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client sub-tab card grid
// ---------------------------------------------------------------------------

function ClientCardGrid({
  rows,
  seen,
  onReview,
}: {
  rows: ClientRow[];
  seen: Set<string>;
  onReview: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="w-6 h-6" />}
        title="No client drafts here."
        subtitle={
          <>
            Drafts a <em className="gt-serif-italic">client</em> sends you for
            review will appear here.
          </>
        }
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
      {rows.map((r) => {
        const partnerName =
          r.requester_name ||
          (r.created_by ? r.created_by.slice(0, 8) + '…' : '—');
        const isUnseen = !seen.has(r.id);
        const onClick = () => onReview(r.id);
        return (
          <div
            key={r.id}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
            className="gt-card p-5 flex flex-col gap-3 cursor-pointer"
          >
            <div className="flex items-start gap-2">
              {isUnseen && <span style={{ marginTop: 7 }}><NewDot /></span>}
              <h3
                className="gt-display flex-1"
                style={{
                  fontSize: 15,
                  lineHeight: 1.35,
                  color: 'var(--gt-text-primary)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {r.title}
              </h3>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={r.status} />
              <PriorityPill priority={r.priority} />
            </div>

            <div>
              <MicroLabel>Requester</MicroLabel>
              <div
                style={{
                  fontFamily: 'var(--gt-font-display)',
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: 'var(--gt-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {partnerName}
              </div>
              {r.requester_org && (
                <div className="gt-serif-italic" style={{ fontSize: 12, color: 'var(--gt-text-tertiary)' }}>
                  {r.requester_org}
                </div>
              )}
            </div>

            <div
              className="flex items-center justify-between gt-numerals"
              style={{
                fontSize: 11.5,
                color: 'var(--gt-text-tertiary)',
                paddingTop: 10,
                borderTop: '1px solid var(--gt-divider)',
              }}
            >
              <span>
                Due <span style={{ color: 'var(--gt-text-secondary)' }}>{fmtDate(r.due_date)}</span>
              </span>
              <span>{fmtRelative(r.last_activity_at || r.created_at)}</span>
            </div>

            <div className="flex items-center justify-end pt-1">
              <span
                className="gt-eyebrow inline-flex items-center gap-1"
                style={{ marginBottom: 0, color: 'var(--gt-primary)' }}
              >
                Review
                <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vendor sub-tab card grid
// ---------------------------------------------------------------------------

function VendorCardGrid({
  rows,
  seen,
  onOpen,
  onReload,
  onConfirm,
}: {
  rows: VendorRow[];
  seen: Set<string>;
  onOpen: (row: VendorRow) => void;
  onReload: () => void;
  onConfirm: (state: ConfirmState) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="w-6 h-6" />}
        title="No tasks sent to vendors yet."
        subtitle={
          <>
            Tasks you submit to a linked <em className="gt-serif-italic">vendor</em> will
            appear here so you can track their status.
          </>
        }
      />
    );
  }

  const archive = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onConfirm({
      title: 'Archive this task?',
      description: 'It will be hidden from your list. The other side will still see it.',
      confirmLabel: 'Archive',
      tone: 'default',
      onConfirm: async () => {
        await tasksApi.archiveTaskForMe(id);
        onReload();
      },
    });
  };

  const deleteTask = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onConfirm({
      title: 'Delete this task?',
      description: 'It will move to Recently Deleted and disappear from both your and the partner’s lists.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        await tasksApi.deleteMyPartnerTask(id);
        onReload();
      },
    });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
      {rows.map((r) => {
        const isInbound = r.direction === 'inbound';
        const isUnseen = !seen.has(r.id);
        return (
          <div
            key={r.id}
            onClick={() => onOpen(r)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r); }}
            className="gt-card p-5 flex flex-col gap-3 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0 flex-1">
                {isUnseen && <span style={{ marginTop: 7 }}><NewDot /></span>}
                <h3
                  className="gt-display flex-1"
                  style={{
                    fontSize: 15,
                    lineHeight: 1.35,
                    color: 'var(--gt-text-primary)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {r.title}
                </h3>
              </div>
              {isInbound && (
                <span className="gt-pill is-personal" style={{ flexShrink: 0 }}>
                  incoming
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={r.status} />
              <PriorityPill priority={r.priority} />
            </div>

            <div>
              <MicroLabel>{isInbound ? 'From vendor' : 'Sent to'}</MicroLabel>
              <div
                style={{
                  fontFamily: 'var(--gt-font-display)',
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: 'var(--gt-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.target_org_name || (
                  <span className="gt-serif-italic" style={{ color: 'var(--gt-text-tertiary)' }}>
                    Unknown partner
                  </span>
                )}
              </div>
            </div>

            <div
              className="flex items-center justify-between gt-numerals"
              style={{
                fontSize: 11.5,
                color: 'var(--gt-text-tertiary)',
                paddingTop: 10,
                borderTop: '1px solid var(--gt-divider)',
              }}
            >
              <span>
                Due <span style={{ color: 'var(--gt-text-secondary)' }}>{fmtDate(r.due_date)}</span>
              </span>
              <span>{fmtRelative(r.created_at)}</span>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => archive(r.id, e)}
                  className="gt-eyebrow"
                  style={{
                    marginBottom: 0,
                    color: 'var(--gt-text-secondary)',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                >
                  Archive
                </button>
                {!isInbound && (
                  <>
                    <span style={{ color: 'var(--gt-text-disabled)' }}>·</span>
                    <button
                      type="button"
                      onClick={(e) => deleteTask(r.id, e)}
                      className="gt-eyebrow"
                      style={{
                        marginBottom: 0,
                        color: 'var(--gt-dnd)',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
              <span
                className="gt-eyebrow inline-flex items-center gap-1"
                style={{ marginBottom: 0, color: 'var(--gt-primary)' }}
              >
                Open
                <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
